<template>
  <div
    class="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg sm:rounded-xl overflow-hidden"
  >
    <div class="border-b border-gray-200 px-4 py-4 sm:px-6 sm:py-5">
      <h3
        class="text-base sm:text-lg font-semibold leading-6 text-gray-900 flex items-center"
      >
        <ChatBubbleLeftRightIcon class="h-6 w-6 text-green-600 mr-3" />
        {{ t("security.wechat.title") }}
      </h3>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <a-spin size="large" />
    </div>

    <!-- 未绑定状态 -->
    <div v-else-if="!wechatBinding" class="text-center py-8 px-4 sm:px-6">
      <ChatBubbleLeftRightIcon class="mx-auto h-12 w-12 text-gray-300" />
      <h3 class="mt-2 text-sm font-semibold text-gray-900">
        {{ t("security.wechat.notBound") }}
      </h3>
      <p class="mt-1 text-sm text-gray-500">
        {{
          t("security.wechat.notBoundDesc", {
            systemShortName: brandConfig.systemShortName,
          })
        }}
      </p>
      <!-- 去绑定按钮 -->
      <div class="mt-4">
        <a-button type="primary" size="small" @click="goToWechatBinding">
          {{ t("security.wechat.goBind") }}
        </a-button>
      </div>
    </div>

    <!-- 已绑定状态 -->
    <div v-else class="px-4 py-4 sm:px-6 sm:py-5">
      <div class="flex items-start space-x-4">
        <!-- 微信头像 -->
        <div class="flex-shrink-0">
          <img
            v-if="wechatBinding.headimgurl"
            :src="wechatBinding.headimgurl"
            :alt="wechatBinding.nickname"
            class="h-12 w-12 rounded-full"
            @error="handleImageError"
          />
          <div
            v-else
            class="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center"
          >
            <ChatBubbleLeftRightIcon class="h-6 w-6 text-green-600" />
          </div>
        </div>

        <!-- 绑定信息 -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between">
            <h4 class="text-sm sm:text-base font-medium text-gray-900">
              {{ wechatBinding.nickname || t("security.wechat.unknownUser") }}
            </h4>
            <a-tag color="green" class="ml-2">
              {{ t("security.wechat.bound") }}
            </a-tag>
          </div>

          <div class="mt-1 sm:mt-2 space-y-1">
            <!-- 绑定时间 -->
            <div class="flex items-center text-sm text-gray-500">
              <ClockIcon class="mr-2 h-4 w-4 flex-shrink-0 text-gray-400" />
              {{ t("security.wechat.bindTime") }}:
              {{ formatDateTime(wechatBinding.bindTime) }}
            </div>

            <!-- 最后更新时间 -->
            <div class="flex items-center text-sm text-gray-500">
              <ArrowPathIcon class="mr-2 h-4 w-4 flex-shrink-0 text-gray-400" />
              {{ t("security.wechat.lastUpdate") }}:
              {{ formatDateTime(wechatBinding.lastUpdateTime) }}
            </div>
          </div>

          <!-- 操作按钮 -->
          <div class="mt-4 flex gap-2">
            <a-button
              type="primary"
              danger
              size="small"
              :loading="unbinding"
              @click="showUnbindConfirm"
            >
              {{ t("security.wechat.unbind") }}
            </a-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 解绑确认弹窗 -->
    <a-modal
      v-model:open="unbindModalVisible"
      :title="t('security.wechat.unbindConfirmTitle')"
      :confirm-loading="unbinding"
      @ok="handleUnbind"
      @cancel="cancelUnbind"
    >
      <div class="space-y-4">
        <div class="flex items-start space-x-3">
          <ExclamationTriangleIcon
            class="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5"
          />
          <div>
            <p class="text-sm text-gray-900">
              {{
                isRebindFlow
                  ? t("user.wechatRebindConfirm")
                  : t("security.wechat.unbindConfirmDesc")
              }}
            </p>
            <div v-if="isRebindFlow" class="mt-3 p-3 bg-blue-50 rounded-md">
              <p class="text-xs text-blue-700 font-medium mb-2">
                {{ t("user.wechatRebindDesc") }}
              </p>
              <div class="text-xs text-blue-600 space-y-1">
                <div>• {{ t("security.wechat.cooldownDesc") }}</div>
                <div>• {{ t("security.wechat.freeLoginDesc") }}</div>
                <div>• {{ t("security.wechat.securityWarningDesc") }}</div>
              </div>
            </div>
            <div class="mt-3 p-3 bg-gray-50 rounded-md">
              <div class="flex items-center space-x-3">
                <img
                  v-if="wechatBinding?.headimgurl"
                  :src="wechatBinding.headimgurl"
                  :alt="wechatBinding.nickname"
                  class="h-8 w-8 rounded-full"
                />
                <div
                  v-else
                  class="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center"
                >
                  <ChatBubbleLeftRightIcon class="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <div class="text-sm font-medium text-gray-900">
                    {{
                      wechatBinding?.nickname ||
                      t("security.wechat.unknownUser")
                    }}
                  </div>
                  <div class="text-xs text-gray-500">
                    {{ t("security.wechat.bindTime") }}:
                    {{ formatDateTime(wechatBinding?.bindTime) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex gap-2">
          <a-button @click="cancelUnbind">
            {{ t("common.cancel") }}
          </a-button>
          <a-button
            type="primary"
            danger
            :loading="unbinding"
            @click="handleUnbind"
          >
            {{
              isRebindFlow
                ? t("user.confirmWechatRebind")
                : t("security.wechat.unbind")
            }}
          </a-button>
        </div>
      </template>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useBrandConfig } from "@/utils/brand";
import { useRouter, useRoute } from "vue-router";
import { notificationService } from "@/services/notificationService";
import {
  ChatBubbleLeftRightIcon,
  ClockIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from "@heroicons/vue/24/outline";
import {
  getWechatOpenidByUserId,
  getWechatBinding,
  unbindWechatOpenid,
} from "@/api/wechat";
import type { WechatUserBinding } from "@/api/wechat";
import { useAuthStore } from "@/stores/auth";

// Props
interface Props {
  userId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  userId: "",
});

// Emit events
const emit = defineEmits<{
  unbindSuccess: [];
  unbindError: [error: unknown];
}>();

const { t } = useI18n();
const brandConfig = useBrandConfig();
const router = useRouter();
const route = useRoute();

// 状态
const loading = ref(false);
const unbinding = ref(false);
const unbindModalVisible = ref(false);
const wechatBinding = ref<WechatUserBinding | null>(null);
const currentOpenid = ref<string | null>(null);
const wechatOpenidForUnbind = ref<string | null>(null);

// 计算属性：是否为换绑流程
const isRebindFlow = computed(() => {
  return (
    wechatOpenidForUnbind.value &&
    wechatOpenidForUnbind.value !== currentOpenid.value
  );
});

// 加载微信绑定信息
const loadWechatBinding = async () => {
  if (!props.userId) {
    wechatBinding.value = null;
    currentOpenid.value = null;
    return;
  }

  try {
    loading.value = true;

    // 首先通过用户ID获取openid
    const openidResponse = await getWechatOpenidByUserId(props.userId);

    if (!openidResponse.openid) {
      // 用户未绑定微信
      wechatBinding.value = null;
      currentOpenid.value = null;
      console.log("用户未绑定微信:", props.userId);
      return;
    }

    currentOpenid.value = openidResponse.openid;

    // 然后通过openid获取详细的绑定信息
    const binding = await getWechatBinding(openidResponse.openid);
    wechatBinding.value = binding;

    console.log("成功加载微信绑定信息:", {
      userId: props.userId,
      openid: openidResponse.openid.substring(0, 8) + "***",
      nickname: binding.nickname,
    });
  } catch (error: unknown) {
    console.error("加载微信绑定信息失败:", error);
    const axiosError = error as {
      response?: { status?: number; data?: { message?: string } };
    };

    // 如果是404错误或者返回消息表示未绑定，说明用户未绑定微信
    if (
      axiosError.response?.status === 404 ||
      axiosError.response?.data?.message?.includes("还未绑定")
    ) {
      wechatBinding.value = null;
      currentOpenid.value = null;
      console.log("用户未绑定微信账号:", props.userId);
    } else {
      notificationService.error("security.wechat.loadError");
    }
  } finally {
    loading.value = false;
  }
};

// 格式化日期时间
const formatDateTime = (dateTime?: string) => {
  if (!dateTime) return "";

  try {
    const date = new Date(dateTime);
    return date.toLocaleString("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateTime;
  }
};

// 处理头像加载错误
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  img.style.display = "none";
};

// 显示解绑确认弹窗
const showUnbindConfirm = async () => {
  try {
    // 检查是否有微信openid需要换绑
    const { wechatService } = await import("@/services/wechatService");
    const wechatOpenid = wechatService.getOpenidFromStorage();

    if (wechatOpenid && wechatOpenid !== currentOpenid.value) {
      // 有不同的微信openid，需要先解绑才能换绑
      wechatOpenidForUnbind.value = wechatOpenid;
    } else {
      // 没有其他openid，正常解绑
      wechatOpenidForUnbind.value = currentOpenid.value;
    }

    unbindModalVisible.value = true;
  } catch (error) {
    console.error("Failed to check wechat service:", error);
    // 如果检查失败，默认使用当前的openid
    wechatOpenidForUnbind.value = currentOpenid.value;
    unbindModalVisible.value = true;
  }
};

// 处理解绑操作
const handleUnbind = async () => {
  if (!wechatOpenidForUnbind.value) {
    return;
  }

  try {
    unbinding.value = true;
    await unbindWechatOpenid(wechatOpenidForUnbind.value);

    notificationService.success("security.wechat.unbindSuccess");
    unbindModalVisible.value = false;

    // 检查是否需要启动换绑流程
    const { wechatService } = await import("@/services/wechatService");
    const storageOpenid = wechatService.getOpenidFromStorage();

    if (storageOpenid && storageOpenid === wechatOpenidForUnbind.value) {
      // 解绑的是存储中的openid，需要启动换绑流程
      const authStore = useAuthStore();
      authStore.startWechatRebinding(wechatOpenidForUnbind.value);

      // 跳转到专门的微信换绑页面
      router.push({
        name: "wechat-rebind",
        query: route.query, // 传递当前查询参数，如redirect等
      });
    } else {
      // 解绑的是当前绑定的openid，正常解绑
      wechatBinding.value = null;
      currentOpenid.value = null;
      emit("unbindSuccess");
    }
  } catch (error: unknown) {
    console.error("解绑微信失败:", error);
    const axiosError = error as { response?: { data?: { message?: string } } };
    notificationService.error(
      axiosError.response?.data?.message || "security.wechat.unbindError",
    );
    emit("unbindError", error);
  } finally {
    unbinding.value = false;
  }
};

// 取消解绑
const cancelUnbind = () => {
  unbindModalVisible.value = false;
  wechatOpenidForUnbind.value = null;
};

// 去绑定微信
const goToWechatBinding = () => {
  router.push({
    name: "wechat-authorize",
    query: route.query, // 传递当前查询参数
  });
};

// 监听userId变化
watch(
  () => props.userId,
  (newUserId) => {
    if (newUserId) {
      loadWechatBinding();
    } else {
      wechatBinding.value = null;
      currentOpenid.value = null;
    }
  },
  { immediate: true },
);

// 组件挂载时加载数据
onMounted(() => {
  if (props.userId) {
    loadWechatBinding();
  }
});

// 暴露方法供父组件调用
defineExpose({
  loadWechatBinding,
});
</script>
